import { useRef, useState, type ReactNode } from 'react';
import { isRealDate } from '@/components/catalog/form-utils';
import {
  CURRENCY_PATTERN,
  DOCUMENT_LINE_AMOUNT_OPTIONS,
  USE_COMPANY_DEFAULT,
  documentLineAmountLabel,
  toDocumentLineAmountType,
} from '@/components/documents/document-form';
import { CreditedTo } from '@/components/documents/CreditedTo';
import { StatusBadge } from '@/components/invoices/InvoiceBits';
import { InlineField, type InlineSaveResult } from '@/components/ui/InlineField';
import { Notice } from '@/components/ui/Notice';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { formatDateOnly, toDateInputValue } from '@/lib/format';
import { denialReason } from '@/lib/permissions';
import {
  currencyOptions,
  selectableStatuses,
  useCurrencyOptions,
  useInvoiceStatusOptions,
} from '@/lib/use-invoice-options';
/*
 * ⚠️ `use-user-options` is deliberately NOT imported any more. It fed exactly
 * one control — the Owner picker — which the read-only Account lead row
 * replaced. The account lead arrives resolved on the record, so there is no
 * staff directory to read and no request to spend against the shared per-IP
 * limiter for an option list nothing renders. ⚠️ *SUPERSEDED: "120/60s."* It
 * is **12,000/60s** (corrected 2026-09-17); the list is still one nothing
 * renders, which is the whole of the reason.
 */
import { brandingThemeOptions, useXeroBrandingThemes } from '@/lib/use-xero-branding-themes';
import { useAuth } from '@/lib/use-auth';
import { useXero } from '@/lib/xero-context';
import type { Invoice, InvoiceUpdateInput } from '@/lib/types';
import type { XeroPushReport } from '@/lib/xero';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * An invoice's HEADER fields, edited where they are read.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The same shape `CompanyDetailPage` uses for a company: one `InlineField` per
 * column, one key per PATCH, an untouched field sending nothing, and a failed
 * save keeping what was typed. `InlineField` owns the interaction; this file
 * owns which keys exist, what each one may hold, and how a refusal reads.
 *
 * It edits the HEADER and nothing else. Line items are a separate surface with
 * a separate owner — see the money section below for why that separation is
 * correctness rather than tidiness.
 *
 * ## ⚠️ THE FOUR TOTALS ARE NOT HERE, AND THAT IS DELIBERATE
 *
 * `subTotal`, `discountTotal`, `taxTotal` and `grandTotal` are STORED columns
 * with **two competing writers**: a line-item write rewrites all four from the
 * lines in one transaction, and a header PATCH writes exactly what it is given
 * and never re-derives. So an invoice has to have ONE owner of its totals, and
 * an inline editor cannot know which owner this invoice has — it is handed a
 * record, not a line count.
 *
 * Three things follow, and only the first is about this file:
 *
 * - **Nothing here offers a totals control.** Whether they may be edited in
 *   place at all is a live decision belonging to another workstream. Until it
 *   lands, not offering them is the only answer that cannot destroy a figure.
 * - **`isLegacyImported()` cannot even be evaluated here.** It needs the line
 *   list, which this component does not fetch and must not — the Zoho imports
 *   carry real money and no lines, and the first line posted against one
 *   replaces all four totals with no confirmation and no undo. A control that
 *   could trip that is not built.
 * - **If totals are ever added here, `subTotal` and `grandTotal` INVERT the
 *   clearing rule.** They are `NOT NULL DEFAULT 0.00`, so `null` passes
 *   validation and takes the column default: it silently writes `0.00` over a
 *   real figure rather than erroring. `UNCLEARABLE_TOTALS` in
 *   `@/components/documents/document-form` is the list, and an emptied box must
 *   be refused client-side. `discountTotal` and `taxTotal` are genuinely
 *   nullable and null there means "not recorded".
 *
 * ## Why there is no read-back check, unlike `CompanyDetailPage`
 *
 * That page compares the saved record against what it sent, because
 * `PATCH /companies/:id` is **not** `.strict()`: it strips an unknown key and
 * still answers 200, so a success does not prove the change applied. **Every
 * invoice write schema IS `.strict()`** — an unrecognised key is a 422 naming
 * it — so a 200 here does prove it. Duplicating that guard would be a check for
 * a hazard this endpoint does not have, and it would cry wolf on any value the
 * server legitimately normalises.
 *
 * ## Validation is COPIED, never re-derived
 *
 * Every rule below is `InvoiceFormModal`'s, imported where it is exportable and
 * mirrored where it is not. Two rules for one column is how they drift, and the
 * drift is only ever discovered as a 422 someone meets after typing.
 *
 * ## ⚠️ THE KEYS THAT ARE DELIBERATELY NOT EDITABLE HERE
 *
 * An absent field looks exactly like an oversight, so every one of them is
 * written down with its reason. Do not add one back on the strength of it being
 * writable on the wire.
 *
 * - **`subTotal` / `discountTotal` / `taxTotal` / `grandTotal`** — two competing
 *   writers, and the decision on whether they may be inline at all belongs to
 *   another workstream. See the money section above.
 * - **`quoteId`** — ⚠️ **the one designed to be missed.** It is a real writable
 *   nullable id on `InvoiceCreateInput`, and it appears NOWHERE in
 *   `InvoiceFormModal`, so anyone deriving the editable set from that form never
 *   meets it and anyone deriving it from `types.ts` sees an ordinary foreign
 *   key. It is not one: it is the invoice's provenance back to the quote that
 *   billed it, and `GET /invoices?quoteId=N` returning anything at all is what
 *   makes a second `POST /quotes/{id}/convert` a 409 and disables the Convert
 *   button. **Clearing it silently unblocks a duplicate conversion** — a second
 *   invoice for work already billed, produced by a control that looked like a
 *   tidy-up.
 * - **`xeroInvoiceId`** — the Xero GUID. Same shape as the quote's: a
 *   hand-typed value there is the only thing the column will ever hold, it
 *   looks authoritative, and it means nothing. The real GUID lives on the link
 *   row and is read with `linkOf(invoice)`.
 * - ⚠️ **`xeroInvoiceNumber`** — was editable here until 2026-08-29, and is now
 *   server-only on the owner's ruling. ⚠️ **This REVERSES a decision written up
 *   at length in `CLAUDE.md` and in `types.ts`**, which held that the column had
 *   three legitimate writers and that locking it removed the only way to record
 *   an invoice raised inside Xero. Both premises expired: the Zoho rows carrying
 *   hand-entered numbers were deleted in the 2026-08-28 reset, and **linking an
 *   invoice now fills the column from the Xero document** (backend `d7585e2`),
 *   so that third case has a server-side route. One writer remains and it is not
 *   this client; the backend's `invoice-number-lockdown` makes the key a 422.
 *   The guard is structural — the key is not in `TEXT_MAX`, so it is not a
 *   `TextKey`, not a `WriteKey`, and no `InlineInvoicePatch` can carry it.
 *   **The VALUE still renders**, read-only: it is the reference finance reads
 *   out on the phone, and losing the control is not a reason to lose the number.
 * - **`currencyRate`** — not EDITABLE here, though it is CLEARED here. The modal
 *   deliberately puts setting it behind its own dialog, because the value is
 *   pushed to Xero VERBATIM as `Invoice.CurrencyRate` and the direction Xero
 *   means by it is unconfirmed; a thin second editor for the same column would
 *   be a second wording of a rule that is still open. Clearing is a different
 *   act and is not optional: a rate is pinned AGAINST a currency, so changing
 *   the currency sends `currencyRate: null` in the same body. See the currency
 *   field — that is a documented exception to "one key per PATCH", not a
 *   second editor.
 * - **`companyId` / `contactId` / `dealId`** — the modal resolves these against
 *   the server with a debounced search. `InlineField`'s picker filters a list it
 *   is handed, so an inline version would silently offer only the first page —
 *   and a record missing from a picker is indistinguishable from a record that
 *   does not exist. They stay in the Edit dialog.
 * - ⚠️ **`projectId`** — the header project. **An invoice is not connected to a
 *   project as a whole; its LINE ITEMS are**, exactly as on a quote, and
 *   `InvoiceLineItemsTable` already owns that per line together with the line's
 *   own cities. The picker that stood here is gone, and the guard is that
 *   **`projectId` is not a member of `WriteKey`** — so `InlineInvoicePatch`
 *   cannot express it, no `onSave` below can build a body carrying it, and a
 *   call site that tries fails at `tsc`. Deliberately NOT an `if` or a
 *   `delete patch.projectId`: a guard someone can forget is not a guard.
 *
 *   The reason it has to be structural is that **removing a control does not
 *   remove a write.** Left reachable, the key goes out as `null` on an ordinary
 *   edit, and an explicit `null` CLEARS the column — taking with it the cities
 *   this document derives through that project. A 200, no error, nothing to
 *   restore from. Same shape as `QuoteFormModal` and `DealFormModal`, which keep
 *   `projectId` off their form-values type for the identical reason.
 *
 *   The key still EXISTS on the API and is still read — `invoice.project` and
 *   the derived `invoice.cities` are live, and the header project remains the
 *   default a new line starts from and the fallback for a document with no
 *   lines. This component simply cannot write it.
 * - **`cityIds`** — does not exist on an invoice. The cities are DERIVED through
 *   the header project, and sending the key is a 422 naming it. Nothing here
 *   may offer to edit that list: it is stored nowhere and a consumer form
 *   editing it would be editing a derivation.
 * - **`accountLeadId`** — not written from here, and it is now the field this
 *   section DISPLAYS in the position the Owner picker used to hold. What is
 *   rendered is the RESOLVED lead: the invoice's own override when it has one,
 *   otherwise its company's, so reassigning an account re-points it.
 *
 *   ⚠️ **CORRECTION (2026-08-30): this bullet used to end "a write naming it is
 *   a 422 on all four revenue documents", and that is FALSE.** It is a 422 on a
 *   DELIVERABLE only, which has no such column. Quotes, invoices and deals all
 *   gained a real `account_lead_id` override — migration 082 here, 079 on deals
 *   — and `accountLeadId` is on the invoice write schema as `optionalUserId()`
 *   (`invoices.schema.ts:200`, verified from backend source). `null` there
 *   means DERIVE, not "no lead". The claim was true when written and was
 *   falsified by a migration, with nothing failing.
 *
 *   **So the reason there is nothing to seed, fetch or send is the OWNER'S
 *   DECISION, not the contract**: owner-assignment was retired on quotes and
 *   invoices in favour of the company's lead shown read-only. The behaviour is
 *   right and stays; only the justification was wrong. Should an override ever
 *   be wanted on one invoice, the API already accepts it — that is frontend
 *   work, not a backend gap to file.
 * - ⚠️ **`ownerId`** — no longer written from here, on the owner's instruction,
 *   matching `InvoiceFormModal`. **This is NOT a rename**: `owner` and
 *   `accountLead` are two different people answering two different questions
 *   (quote 9: owner Paras Fatnani, account lead Daryna Kodinets), so a record
 *   read before and after this change shows a DIFFERENT NAME in the same place.
 *   `invoice.owner` is still stored and still read; it is simply not writable
 *   here. The guard is the same structural one `projectId` gets: **`ownerId` is
 *   not a member of `WriteKey`**, so `InlineInvoicePatch` cannot express it and
 *   no `saveField` call can build a body carrying it. Deleting the JSX alone
 *   would have left the write reachable — and an `ownerId` reaching a body as
 *   `null` CLEARS the column with a 200.
 */

export interface InlineInvoiceFieldsProps {
  invoice: Invoice;
  /**
   * The saved record. Called once per successful write, and only for the newest
   * write still in flight — see `writeSeq`. This component holds no copy of the
   * invoice; the page owns it.
   */
  onSaved: (invoice: Invoice) => void;
  /**
   * Hands the page the SERVER'S report of what that save did to Xero.
   *
   * ⚠️ **Added 2026-08-30 with the move of the push onto the server.** The
   * PATCH this component makes pushes the invoice inside the same request and
   * answers `xeroPush` beside `data` — see `SaveEnvelope` in `@/lib/types`.
   * Nothing in this file calls a push endpoint, and no copy here may read as
   * though it did; what this prop closes is the SILENCE, not the push.
   *
   * ⚠️ **The RAW server report goes up, not a classified outcome** — the same
   * contract `InvoiceFormModalProps.onXeroPush` states, and deliberately the
   * same prop name, shape and reasoning as `InlineQuoteFieldsProps.onXeroPush`.
   * Two spellings of one idea is how this section and that modal come to
   * disagree about the same save. The vocabulary belongs to `@/lib/xero` and
   * the copy belongs to the page, so `autoPushOutcomeOf` is read in ONE place.
   *
   * ⚠️ **It cannot fail or roll back the save, and nothing built on it may be
   * worded as though it could.** The invoice is stored either way, and Xero
   * does not enforce invoice-number uniqueness — somebody who reads a failed
   * push as a lost save and saves again can end up with two live demands for
   * money against one customer.
   *
   * ⚠️ **Only the NEWEST write in flight reports**, exactly as `onSaved` does —
   * see `writeSeq`. A report from a save that has already been superseded is a
   * statement about a version of the invoice nobody is looking at. `undefined`
   * is what an older server, or a rollback, sends, and it is an ordinary answer
   * rather than an error.
   */
  onXeroPush?: (report: XeroPushReport | undefined) => void;
}

/**
 * Server-enforced maximums, mirrored from `InvoiceFormModal.MAX_LENGTHS` so the
 * control stops where the API would.
 *
 * ⚠️ Only the keys this component actually edits are here. `xeroInvoiceId` is
 * absent because the field is not offered (see the exclusions below), and a
 * limit for a control that does not exist is a rule nothing can reach.
 *
 * ⚠️ **`xeroInvoiceNumber` is absent, and its absence IS the guard.** This map
 * keys `TextKey`, `TextKey` feeds `WriteKey`, and `WriteKey` narrows
 * `InlineInvoicePatch` — so dropping the entry makes the key inexpressible in
 * any body this file can build, and `tsc` refuses a call site that tries. It
 * became server-only on the owner's ruling (2026-08-29); the backend's
 * `invoice-number-lockdown` makes it a 422, and it is now READ here and never
 * written. See the note on `InvoiceCreateInput` in `types.ts` for why that
 * reverses an earlier decision.
 */
const TEXT_MAX = {
  subject: 255,
  /** The CUSTOMER's PO reference. `VARCHAR(255)`. */
  reference: 255,
  /** Customer-facing wording. `TEXT`, and genuinely 16,000 — not a typo. */
  terms: 16000,
  /*
   * 100, deliberately wider than Xero's own 50-character tracking-option limit:
   * an over-long value must fail as a 422 naming the field when it is checked
   * against the live option list, rather than being silently truncated into
   * something that looks like a real option.
   */
  xeroTrackingLocation: 100,
  xeroTrackingTourType: 100,
} as const;

type TextKey = keyof typeof TEXT_MAX;

/** The co-validated date pair. Neither is ever written without the other. */
type DateKey = 'billDate' | 'dueDate';

/**
 * ⚠️ **THE WRITE LIST, AND IT IS THE GUARD — not documentation of one.**
 *
 * Every key this component may put in a PATCH body. `InlineInvoicePatch` is
 * `InvoiceUpdateInput` narrowed to exactly these, and `saveField` accepts
 * nothing else, so a key absent from this union **cannot be expressed by any
 * `onSave` in this file** and an attempt to add one is a compile error rather
 * than a review comment.
 *
 * ⚠️ **`projectId` is absent, and its absence IS the mechanism.** See the
 * module comment: an invoice's project belongs to its LINE ITEMS, and a
 * `projectId` that could still reach a body would go out as `null` on an
 * ordinary edit and silently clear the column and the cities derived through
 * it. `QuoteFormModal` and `DealFormModal` keep the key off their value types
 * for the same reason. Do not widen this union to "tidy up" a type error;
 * the type error is the point.
 *
 * ⚠️ **`ownerId` is absent for the identical reason.** The Owner picker was
 * replaced by a read-only Account lead row (see the module comment), and
 * removing a control does not remove a write — a reachable `ownerId` would go
 * out as `null` from any stray call site and clear the column with a 200.
 * `accountLeadId` is not here either — but for a DIFFERENT reason, and the
 * distinction matters. ⚠️ **CORRECTION (2026-08-30): this said "and never can
 * be: it is a 422 on a write to an invoice", which is FALSE.** Migration 082
 * gave `d_invoices` a real `account_lead_id` override and the key is on the
 * write schema (`invoices.schema.ts:200`, `optionalUserId()`); `null` clears
 * the override rather than blanking the field. `ownerId` is absent because a
 * stray write would CLEAR a column — a data-safety guard. `accountLeadId` is
 * absent because the owner retired lead-assignment on this document in favour
 * of the company's, shown read-only — a product decision. Keeping it off this
 * union is still right, since no control writes it and an inexpressible key
 * beats a forgettable guard; it is simply not the API refusing us.
 *
 * It is also the SEND list rather than the render list, so it doubles as the
 * set a 422 may name and be marked on a control: a path naming a key with no
 * editor here is unroutable and belongs in the section banner.
 */
type WriteKey =
  | TextKey
  | DateKey
  | 'statusId'
  | 'currency'
  | 'currencyRate'
  | 'xeroLineAmountType'
  | 'xeroBrandingThemeId';

/**
 * A body this component is allowed to send.
 *
 * Narrowed from `InvoiceUpdateInput` rather than used raw: the full type is the
 * whole wire contract, which includes `projectId`, the four totals and
 * `quoteId` — all of them deliberately not editable here, and all of them
 * destructive to send by accident.
 */
type InlineInvoicePatch = Pick<InvoiceUpdateInput, WriteKey>;

/**
 * The client-side rules for the text columns, mirrored from
 * `InvoiceFormModal.validate()`.
 *
 * `subject` is the one key here that is NOT nullable server-side — `subject:
 * null` is a 422 — so it is refused empty rather than cleared.
 */
function validateText(key: TextKey, next: string): string | null {
  if (next.length > TEXT_MAX[key]) {
    return `Must be ${TEXT_MAX[key].toLocaleString()} characters or fewer.`;
  }
  if (key === 'subject' && next === '') return 'Subject is required.';
  return null;
}

/**
 * The body for one text column, and nothing else.
 *
 * PATCH semantics decide the shape: an omitted key is left untouched and an
 * explicit `null` clears the column, so a per-field save sends exactly one key.
 * Never a spread of the loaded record — every invoice write schema is
 * `.strict()`, so a read-only key from a GET would be a 422 rather than a
 * silent strip, and the response carries resolved objects where the write takes
 * ids.
 */
function textPatch(key: TextKey, next: string): InlineInvoicePatch {
  // Not nullable server-side; the validator above keeps it non-empty.
  if (key === 'subject') return { subject: next };
  const patch: InlineInvoicePatch = {};
  patch[key] = next === '' ? null : next;
  return patch;
}

/** Empty strings read back as the API's `null`. */
function stored(value: string | null): string {
  return value ?? '';
}

export function InlineInvoiceFields({
  invoice,
  onSaved,
  onXeroPush,
}: InlineInvoiceFieldsProps) {
  const { ability } = useAuth();
  const canUpdate = ability.canUpdate('invoices');

  /*
   * The option sources are only asked for when something can be chosen. A
   * read-only viewer renders the same values with no controls, so every list
   * request would be a request for data nothing can use. Each hook's
   * `enabled` flag already handles being switched on later.
   */
  const { statuses, loading: statusesLoading, failed: statusesFailed } =
    useInvoiceStatusOptions(canUpdate);
  const { currencies, loading: currenciesLoading, failed: currenciesFailed } =
    useCurrencyOptions(canUpdate);
  /*
   * ⚠️ No `useUserOptions` here. The Owner picker it fed is gone, replaced by
   * the read-only Account lead row below, and that value is resolved on the
   * record — so a staff-directory read could only produce options nothing may
   * choose from.
   */
  /*
   * ⚠️ No `useProjectOptions` here, and it is not an oversight. This section
   * cannot write `projectId` (see the module comment), so a project list would
   * be a request for options nothing may choose from — and a rendered picker
   * over it is exactly the control this change removed.
   */

  /*
   * The branding-theme list belongs to the LIVE tenant, so it is only asked for
   * once there is a tenant to ask. While the connection read is settling, or
   * when there is no connection, a request could only fail — and "Xero is not
   * connected" is a calm sentence with a different action, not an error.
   */
  const xero = useXero();
  const xeroConnected = !xero.loading && xero.connection.connected;
  const brandingThemes = useXeroBrandingThemes(canUpdate && xeroConnected);

  /**
   * Two quick saves on two different fields can resolve out of order. Each
   * `InlineField` guards its own commit; this guards the RECORD — only the
   * newest response may be published back to the page, or an older invoice
   * would overwrite a newer one in the page's state.
   */
  const writeSeq = useRef(0);

  /**
   * The section-level message: a refusal that belongs to the request rather
   * than to any control on screen.
   *
   * What lands here is the thing a field cannot honestly carry: any
   * `.strict()` or "at least one field" refusal, and any 422 naming a key this
   * save did not send — every one of them has a `path` that binds to no input.
   *
   * ⚠️ The `xeroInvoiceNumber` **409** used to land here too. It cannot arrive
   * any more: the column is UNIQUE, but this component can no longer put the
   * key in a body (see `TEXT_MAX`), so there is nothing here to collide with.
   */
  const [formNotice, setFormNotice] = useState<string | null>(null);

  /**
   * Writes one column and reports what happened, in the vocabulary
   * `InlineField` renders. Nothing here throws: the field needs an outcome, not
   * an exception, and it has no business knowing what an `ApiError` is.
   *
   * `requestKeys` is the list of keys THIS save actually sent — one for every
   * field except the date pair, which sends two. It is what the 422 is split
   * against, so a `path` naming a key this save did not send can never be
   * marked on the control that is open.
   */
  const saveField = async (
    requestKeys: readonly WriteKey[],
    patch: InlineInvoicePatch,
  ): Promise<InlineSaveResult> => {
    const ticket = writeSeq.current + 1;
    writeSeq.current = ticket;
    // A message about the last attempt says nothing about this one.
    setFormNotice(null);

    try {
      const response = await api.updateInvoice(invoice.id, patch);
      if (writeSeq.current === ticket) {
        /*
         * ⚠️ Inside the `writeSeq` guard, and BEFORE `onSaved`. Inside, because
         * a superseded save's report describes a version of the invoice nobody
         * is looking at; before, because `onSaved` is what makes the page
         * re-read the invoice, and a report handed up afterwards would land on
         * a render the refetch had already started.
         */
        onXeroPush?.(response.xeroPush);
        onSaved(response.data);
      }
      return { status: 'saved' };
    } catch (caught) {
      if (caught instanceof ApiError) {
        /*
         * A 403 carries no `details` at all, so it can never be a field error —
         * and it can arrive even though `canUpdate` said otherwise, because a
         * role can change under a live access token, which lasts over five
         * days. A hidden control is an optimisation; this is the guarantee.
         */
        if (caught.status === 403) {
          return {
            status: 'error',
            message: `${
              denialReason(ability, 'update', 'invoices') ??
              'Your account no longer has permission to change this invoice.'
            } Nothing was saved.`,
          };
        }
        if (caught.status === 404) {
          return {
            status: 'error',
            message: 'This invoice no longer exists, so the change could not be saved.',
          };
        }
        /*
         * ⚠️ **The 409 handler that stood here is GONE, because the refusal is
         * unreachable — not because it was noisy.**
         *
         * The only 409 this route raised was a collision on the UNIQUE
         * `xero_invoice_number`, and this component can no longer put that key
         * in a body: it left `TEXT_MAX`, so it left `TextKey`, `WriteKey` and
         * `InlineInvoicePatch` with it. Every remaining key here — the two
         * dates, the four text columns, the status, currency, rate, line-amount
         * type and branding theme — is on a column with no uniqueness rule, so
         * nothing this file sends can collide.
         *
         * A 409 arriving anyway therefore falls through to the server's own
         * message at the bottom of this block, which is the right degradation:
         * an unrecognised refusal means "newer than this branch", and a
         * hardcoded sentence about invoice numbers would name the one thing it
         * could not be about.
         */

        /*
         * Split against the keys THIS save sent, never against the shape of the
         * path. `fields` is bound to a control that is on screen; everything
         * else — the empty-path `.strict()` and "at least one field" refusals,
         * and any path naming a key with no control here — is unroutable and
         * belongs at section level.
         *
         * ⚠️ Routed by `ApiError`'s own message test, NOT by reading the `key`
         * property a 422 detail has carried since 2026-08-27. That field is the
         * exact discriminator and is also unconfirmed as the settled shape —
         * the envelope's geometry has moved three times — and a reader for a
         * field that disappears next week reinstates the problem one level
         * down. `isUnrecognisedKey()` has routed correctly through all three
         * shapes.
         */
        const { fields, unroutable } = caught.routeFieldErrors(requestKeys);
        /*
         * Joined rather than picked. On the date pair the server can name
         * `dueDate` for a change made to `billDate`, and both messages are true
         * at once — a `??` between them would drop one silently.
         */
        const fieldText = Object.values(fields).join(' ');
        if (fieldText !== '') {
          if (unroutable.length > 0) setFormNotice(unroutable.join(' '));
          return { status: 'error', message: fieldText };
        }
        if (unroutable.length > 0) {
          setFormNotice(unroutable.join(' '));
          return {
            status: 'error',
            message:
              'Not saved. The reason is shown with this section.',
          };
        }
        return { status: 'error', message: caught.message };
      }
      return {
        status: 'error',
        message: 'Could not reach the server. Check your connection and try again.',
      };
    }
  };

  /*
   * The fields below are built by plain functions returning elements, never by
   * components declared in here: a component declared inside this one is a new
   * type on every render, so every field would be unmounted — losing its draft
   * and its "Saved" line — the moment anything else on the page changed.
   */

  const textField = (
    key: TextKey,
    label: string,
    extra: {
      display?: ReactNode;
      emptyLabel?: string;
      placeholder?: string;
      multiline?: boolean;
      rows?: number;
    } = {},
  ) => {
    const { multiline, ...rest } = extra;
    return (
      <InlineField
        label={label}
        value={stored(invoice[key])}
        kind={multiline ? 'multiline' : 'text'}
        disabled={!canUpdate}
        maxLength={TEXT_MAX[key]}
        validate={(next) => validateText(key, next)}
        onSave={(next) => saveField([key], textPatch(key, next))}
        {...rest}
      />
    );
  };

  // --- the date pair --------------------------------------------------------

  /**
   * ⚠️ **The one place this component sends TWO keys in one PATCH, and it has
   * to.**
   *
   * `billDate > dueDate` is a 422 the server reports at `dueDate` — but it only
   * runs that check when BOTH dates are present in the same body. A PATCH that
   * moves only `billDate` past a stored `dueDate` therefore succeeds and leaves
   * the invoice inconsistent, silently. So whenever either one changes, both
   * travel: the unchanged half is re-sent at exactly its stored value, which
   * writes nothing new and gives the server the pair to compare.
   *
   * Same rule, and the same reason, as `{companyId, pointOfContactId}` on a
   * deliverable and `{cycleId, intakeId}` on a project: the server only
   * re-checks a pair when it can see the pair.
   */
  function datePatch(key: DateKey, next: string): InlineInvoicePatch {
    const bill = key === 'billDate' ? next : toDateInputValue(invoice.billDate);
    const due = key === 'dueDate' ? next : toDateInputValue(invoice.dueDate);
    return {
      billDate: bill === '' ? null : bill,
      dueDate: due === '' ? null : due,
    };
  }

  /**
   * The date rules, mirrored from `InvoiceFormModal.validate()`.
   *
   * Compared as STRINGS, never as `Date`s. ISO dates sort lexicographically so
   * the comparison is exact, whereas `new Date('2026-05-31')` is midnight UTC
   * and renders as the previous day west of Greenwich — the off-by-one this
   * format exists to avoid. `isRealDate` checks the day actually exists, so
   * `2026-02-30` is refused here exactly as the server refuses it.
   */
  function validateDate(key: DateKey, next: string): string | null {
    if (next !== '' && !isRealDate(next)) return 'Must be a real date in YYYY-MM-DD form.';
    const bill = key === 'billDate' ? next : toDateInputValue(invoice.billDate);
    const due = key === 'dueDate' ? next : toDateInputValue(invoice.dueDate);
    if (bill !== '' && due !== '' && bill > due) {
      return 'The due date cannot be before the bill date.';
    }
    return null;
  }

  const dateField = (key: DateKey, label: string) => {
    /*
     * `toDateInputValue` rather than the raw column, and it is a no-op on the
     * documented shape. An invoice's dates are plain `YYYY-MM-DD`, but this API
     * serialises SOME date-only columns as a full ISO timestamp, and a native
     * date control handed one renders BLANK — which reads as "no date" on a
     * record that has one, and is one save away from clearing it. The slice
     * never touches a timezone, which is the whole reason that helper exists.
     */
    const value = toDateInputValue(invoice[key]);
    return (
      <InlineField
        label={label}
        value={value}
        disabled={!canUpdate}
        /*
         * A native date control, whose value is ALWAYS `YYYY-MM-DD` and empty
         * when cleared — the same plain string the column stores, never a
         * `Date`. `isRealDate` still runs: a partially typed date yields `''`
         * in some browsers rather than a refusal, and the pair rule below has
         * to be checked whatever the control produced.
         */
        type="date"
        display={
          value === '' ? (
            <span className="text-fg-subtle">Not set</span>
          ) : (
            <span className="tabular">{formatDateOnly(value)}</span>
          )
        }
        validate={(next) => validateDate(key, next)}
        /*
         * Both keys are declared as sent, so a 422 naming EITHER lands on the
         * control that is open rather than in the section banner. The pair is
         * one edit to the reader, whichever half they opened.
         */
        onSave={(next) => saveField(['billDate', 'dueDate'], datePatch(key, next))}
      />
    );
  };

  // --- the pickers ----------------------------------------------------------

  /**
   * The status.
   *
   * `/invoice-statuses` is a REFERENCE MASTER, not a deal-style picker: the
   * filter key is `isActive`, `perPage` runs 50/200 rather than 20/100, and
   * rows arrive in `sortOrder, name` order already — `useInvoiceStatusOptions`
   * encodes all of that and fetches the whole list unfiltered, so a status an
   * invoice already holds is never dropped for having been retired.
   * `selectableStatuses` marks it instead.
   *
   * ⚠️ Nothing here reads the status NAME to decide anything. It is
   * admin-writable free text, and `"Unpaid"` contains `"paid"` — the substring
   * test that hid the Overdue badge from unpaid invoices. Where a name IS read,
   * it is matched exactly after case-folding, in `StatusBadge` and
   * `isOverdue()`, and this control simply carries the id.
   */
  const statusField = () => (
    <InlineField
      kind="picker"
      label="Status"
      value={invoice.status ? String(invoice.status.id) : ''}
      display={<StatusBadge status={invoice.status} />}
      disabled={!canUpdate}
      options={selectableStatuses(statuses, invoice.status)}
      optionsLoading={statusesLoading}
      emptyOptionLabel="No status"
      placeholder={statusesLoading ? 'Loading statuses…' : 'No status'}
      searchPlaceholder="Search statuses…"
      {...(statusesFailed
        ? {
            footnote: 'The status list couldn’t be loaded, so this picker is empty.',
          }
        : {})}
      onSave={(next) => saveField(['statusId'], { statusId: next === '' ? null : Number(next) })}
    />
  );

  /**
   * ═════════════════════════════════════════════════════════════════════════
   * The ACCOUNT LEAD, read-only — where the Owner picker used to stand.
   * ═════════════════════════════════════════════════════════════════════════
   *
   * The owner's instruction, already applied to `InvoiceFormModal`; this
   * section followed so the two cannot disagree about the same field on the
   * same screen.
   *
   * ⚠️ **This is NOT a rename of "Owner".** `owner` and `accountLead` are two
   * different people answering two different questions — the owner holds this
   * invoice, the account lead holds the relationship with the company (quote 9:
   * owner Paras Fatnani, account lead Daryna Kodinets). Anyone reading an
   * invoice before and after this change sees a DIFFERENT NAME in this
   * position, which is why the label changed with the value and nothing here
   * describes it as the same person under a new heading.
   *
   * ⚠️ **Read-only HERE, by decision — not by refusal.** What is shown is the
   * RESOLVED lead: the invoice's own override where it has one, otherwise its
   * company's. It is not a member of `WriteKey`, so no `InlineInvoicePatch` can
   * express it and there is nothing to fetch, seed or send. It is plain text,
   * not a disabled `InlineField`: an inert control invites someone to try to
   * open it.
   *
   * ⚠️ **CORRECTION (2026-08-30): this read "`accountLeadId` in a write body is
   * a 422 — the value is stored on the COMPANY", and both halves are FALSE for
   * an invoice.** Migration 082 added `d_invoices.account_lead_id` and the key
   * is on the write schema (`invoices.schema.ts:200`, `optionalUserId()`); it
   * is a 422 only on a DELIVERABLE, which genuinely has no column. The
   * sentence was true when written and the migration falsified it without
   * breaking anything — which is exactly why it survived.
   *
   * The read-only rendering is the owner's call — lead-assignment retired on
   * quotes and invoices in favour of the company's — and it stands. Do not
   * conclude from this block that an override is impossible; it is unbuilt.
   *
   * `invoice.owner` is untouched by all of this — still stored, still read on
   * the wire, simply not writable from this section any more.
   */
  const accountLeadField = () => {
    const person = invoice.accountLead;
    if (person === null) {
      /*
       * A company with no account lead recorded, or an invoice with no company.
       * Neutral: this states what is on the record and asserts nothing about
       * who ought to be there.
       */
      return <span className="text-fg-subtle">Not set</span>;
    }
    /*
     * ⚠️ Never `''`. `UserSummary.name` is typed `string` but is empty on real
     * rows, and a blank line over a lead that EXISTS reads as "nobody" — the
     * one thing this row must not say. Email, then the id, so a real person
     * always renders as something.
     */
    const label =
      person.name.trim() !== ''
        ? person.name
        : person.email.trim() !== ''
          ? person.email
          : `Staff #${person.id}`;
    return (
      <span
        className="min-w-0"
        title="Set on the company."
      >
        {label}
      </span>
    );
  };

  /**
   * ═════════════════════════════════════════════════════════════════════════
   * The XERO INVOICE NUMBER, read-only — where an `InlineField` used to stand.
   * ═════════════════════════════════════════════════════════════════════════
   *
   * The owner's ruling, 2026-08-29: the column is written by the server and by
   * nothing else. The push allocates it, and linking an invoice fills it from
   * the Xero document — so the "a person records a number raised inside Xero"
   * case, which was the whole argument for keeping the box, is now served by
   * linking rather than by typing. The backend's `invoice-number-lockdown`
   * makes the key a 422; this app stops sending it first.
   *
   * ⚠️ **Read-only, structurally.** The key is not in `TEXT_MAX`, so it is not
   * a `TextKey`, not a `WriteKey`, and not expressible in an
   * `InlineInvoicePatch`. Deleting the JSX alone would have left the write
   * reachable — the same guard `projectId` and `ownerId` get above.
   *
   * Plain text rather than a disabled `InlineField`, matching the account-lead
   * row: an inert control reads as something the viewer has not earned the
   * right to use, where a value reads as a fact. `tabular` because it is a
   * reference somebody reads out digit by digit.
   */
  const xeroInvoiceNumberValue = () => {
    const number = stored(invoice.xeroInvoiceNumber).trim();
    /*
     * Null on every invoice that has never been pushed or linked, which is the
     * ordinary state rather than a fault. `.trim()` as well, because an empty
     * string in a value slot reads as a missing record rather than an absent
     * one.
     */
    if (number === '') return <span className="text-fg-subtle">Not set</span>;
    return <span className="tabular">{number}</span>;
  };

  /*
   * ═════════════════════════════════════════════════════════════════════════
   * ⚠️ THE HEADER PROJECT PICKER STOOD HERE, AND IT IS NOT COMING BACK.
   * ═════════════════════════════════════════════════════════════════════════
   *
   * The owner's decision: **an invoice is not connected to a project as a
   * whole — its LINE ITEMS are**, exactly as on a quote. A project names one
   * product and a document sells many, so the project belongs to the line that
   * sells it. `InvoiceLineItemsTable` already owns that per line, together with
   * the line's own city set, so nothing was lost by removing this control.
   *
   * The header key `projectId` still EXISTS on the API and is still READ — the
   * page renders `invoice.project` and the derived `invoice.cities`, and the
   * header project remains the default a new line starts from and the fallback
   * for a document with no lines. **This component deliberately cannot express
   * it**: `projectId` is not a member of `WriteKey`, so `InlineInvoicePatch`
   * has no such property and `tsc` refuses any `saveField` call that tries.
   *
   * ⚠️ That absence from the key type IS the guard — not an `if`, not a
   * `delete patch.projectId`. Removing a CONTROL does not remove a WRITE: left
   * reachable, the key would go out as `null` on an ordinary edit, and an
   * explicit `null` CLEARS the column and with it the cities this document
   * derives through that project. A 200, no error, nothing to restore from.
   * `QuoteFormModal` and `DealFormModal` keep the key off their form-values
   * type for the identical reason, and this file follows them.
   *
   * And nothing here may offer to EDIT the derived `cities`: they are stored
   * nowhere, there is no `cityIds` on an invoice, and sending one is a 422
   * naming the key. Displaying them read-only is fine and is the page's job.
   */

  /**
   * The currency.
   *
   * ⚠️ **The picker's VALUE is a `currencyCode` from the `/currencies` master,
   * and it is WRITTEN under the key `currency`.** Those two names have been
   * misread before and neither works in the other place. An invoice stores a
   * `CHAR(3)` code with no foreign key, so an id here would be rejected — or
   * worse, coerced.
   *
   * `^[A-Z]{3}$`, checked exactly and with **no case folding**: the server
   * refuses `"usd"` rather than upper-casing it, deliberately, so the client's
   * intent is explicit. The pattern check cannot fire from a picker whose rows
   * all come from the master; it is here because the modal has it and because
   * a pinned-back stored code could be anything the column holds.
   *
   * Nothing anywhere sums across this field. Currency is per-invoice and is
   * never converted, so one total over mixed currencies is wrong by
   * construction — this control edits one document's own code and offers no
   * aggregate of any kind.
   *
   * ⚠️ **CHANGING THE CURRENCY CLEARS A PINNED RATE, IN THE SAME PATCH.** This
   * is the second documented exception to "one key per PATCH" in this file,
   * alongside the date pair, and it is copied from `InlineQuoteFields` — the
   * same rule, the same shape, deliberately not a second spelling of it.
   *
   * A pinned `currencyRate` belongs to the currency it was pinned against, and
   * it is **pushed to Xero VERBATIM as `Invoice.CurrencyRate`**. Leaving an old
   * rate on a document whose currency has moved puts a fabricated conversion on
   * a customer-facing invoice: a live AUD→INR change that kept the AUD rate had
   * Xero print "GBP 6,398.90" against 12,100 INR, roughly 69× overstated, with
   * a 200 and nothing on either screen looking wrong. `null` clears it honestly
   * — `currency_rate` is genuinely nullable, so `null` is stored as `null`
   * rather than taking a column default the way the two NOT NULL totals would.
   *
   * ⚠️ The key is **OMITTED when no rate is pinned**, so the ordinary invoice
   * stays a one-key write. Sending `currencyRate: null` unconditionally would
   * be a second key on every currency change for no reason, and it would name a
   * field in any 422 that the person never touched.
   */
  const currencyField = () => {
    const pinnedRate = invoice.currencyRate;
    /*
     * The one thing a reader must know BEFORE choosing: this save carries a
     * second key and clears a value they can see elsewhere on the page.
     */
    const rateNote =
      pinnedRate === null
        ? null
        : `Changing the currency clears the pinned rate of ${pinnedRate}.`;
    /*
     * Both notes are said, never coalesced. A failed list and a pinned rate are
     * two independent facts that can be true at once, and a `??` between two
     * things that both need showing is the silent drop this repo keeps finding.
     */
    const notes = [
      currenciesFailed
        ? 'The currency list couldn’t be loaded, so this picker is empty.'
        : null,
      rateNote,
    ].filter((note): note is string => note !== null);

    return (
      <div className="grid min-w-0 gap-1">
        <InlineField
          kind="picker"
          label="Currency"
          value={stored(invoice.currency)}
          display={
            invoice.currency === null ? (
              <span className="text-fg-subtle">Not set</span>
            ) : (
              <span className="tabular">{invoice.currency}</span>
            )
          }
          disabled={!canUpdate}
          options={currencyOptions(currencies, invoice.currency)}
          optionsLoading={currenciesLoading}
          emptyOptionLabel="Not set"
          placeholder={currenciesLoading ? 'Loading currencies…' : 'Not set'}
          searchPlaceholder="Search currencies…"
          {...(notes.length > 0 ? { footnote: notes.join(' ') } : {})}
          validate={(next) =>
            next !== '' && !CURRENCY_PATTERN.test(next.toUpperCase())
              ? 'Use a three-letter ISO code, e.g. USD.'
              : null
          }
          onSave={(next) => {
            const code = next === '' ? null : next.toUpperCase();
            /*
             * ⚠️ TWO keys when a rate is pinned, ONE when it is not. Both the
             * body and the declared `requestKeys` branch together, so a 422
             * naming `currencyRate` is only ever marked on a save that actually
             * sent it.
             */
            return pinnedRate === null
              ? saveField(['currency'], { currency: code })
              : saveField(['currency', 'currencyRate'], { currency: code, currencyRate: null });
          }}
        />
        {/*
          Said outside the popover as well as in it: a footnote only exists once
          the field has been opened, and the rate a save is about to clear is
          something to know BEFORE opening it.
        */}
        {rateNote !== null && canUpdate && (
          <p role="status" className="m-0 text-2xs text-warning">
            {rateNote}
          </p>
        )}
      </div>
    );
  };

  /**
   * Whether this document's line prices INCLUDE tax.
   *
   * ⚠️ The **DOCUMENT** enum (`NOTAX`), never the CONTACT enum (`NONE`) a
   * company's `xeroSalesLineAmountType` carries. A different word, not different
   * casing, and sending the contact spelling is a 422. `toDocumentLineAmountType`
   * narrows rather than casts, so a stored value outside the union — including
   * `NONE` — becomes `null` rather than being smuggled into a write claiming to
   * be a document member.
   *
   * ⚠️ `''` is not a fourth member. It is the ABSENCE of a value, stored as
   * `null`, and it means "use the company's Xero sales default" — never "no
   * tax", which is what `NOTAX` says.
   */
  const lineAmountField = () => {
    const value = stored(invoice.xeroLineAmountType);
    const label = documentLineAmountLabel(value);
    return (
      <InlineField
        kind="picker"
        label="Xero line amounts"
        value={value}
        display={
          value === '' ? (
            <span className="text-fg-subtle">{USE_COMPANY_DEFAULT}</span>
          ) : (
            // A stored value the enum does not recognise still renders as
            // itself: it IS what the column holds, and blanking it would claim
            // the company default is in play when it is not.
            <span className="min-w-0">{label ?? value}</span>
          )
        }
        disabled={!canUpdate}
        options={DOCUMENT_LINE_AMOUNT_OPTIONS.map((option) => ({
          value: option.value,
          label: option.label,
        }))}
        emptyOptionLabel={USE_COMPANY_DEFAULT}
        placeholder={USE_COMPANY_DEFAULT}
        onSave={(next) =>
          saveField(['xeroLineAmountType'], { xeroLineAmountType: toDocumentLineAmountType(next) })
        }
      />
    );
  };

  /**
   * The Xero letterhead this document's PDF prints on.
   *
   * The list belongs to the LIVE tenant, so there is nothing to offer while
   * Xero is disconnected — and that is a calm state, not an error. The stored
   * id is still shown either way: it is what the document carries.
   *
   * ⚠️ **`xeroBrandingThemeName` is not a field on an invoice**, and the server
   * never resolves a name for a stored id, so a theme missing from the live list
   * can only be labelled by its GUID. That reads badly and is still better than
   * a blank row, which reads as a choice.
   *
   * ⚠️ A DOCUMENT's theme id is validated for GUID SHAPE only — unlike a
   * company's, which is checked against the live theme list. So a wrong id here
   * is stored, forwarded to Xero unexamined, and comes back as a theme with no
   * label rather than as an error. Only ever offer ids from the live list, which
   * is what `brandingThemeOptions` does.
   */
  const brandingThemeField = () => {
    const value = stored(invoice.xeroBrandingThemeId);
    const known = brandingThemes.themes.find(
      (theme) => theme.brandingThemeId.toLowerCase() === value.toLowerCase(),
    );
    /*
     * Only the read failure. "Xero is not connected" is said as
     * `disabledReason` instead — a footnote renders inside the popover, which a
     * disabled picker never opens, so saying it here as well would be one
     * sentence in two places and one of them invisible.
     *
     * ⚠️ A rate-limit cooldown is a read failure like any other HERE, and that
     * is the point: the field is not disabled by it, `brandingThemeOptions`
     * still injects the stored id, and the display above still renders that id.
     * The list being unreadable never makes this invoice's stored letterhead
     * unknown. The only addition is a clause saying so, because the shared
     * message describes the LIST and a reader looking at a value wants to know
     * that value is untouched.
     */
    const themeError =
      brandingThemes.error === null
        ? null
        : brandingThemes.error.kind === 'cooldown' && value !== ''
          ? `${brandingThemes.error.message} The branding theme already on this invoice is unchanged, and can still be cleared.`
          : brandingThemes.error.message;

    return (
      <InlineField
        kind="picker"
        label="Xero branding theme"
        value={value}
        display={
          value === '' ? (
            <span className="text-fg-subtle">Organisation default</span>
          ) : (
            <span className="min-w-0">{known?.name ?? value}</span>
          )
        }
        /*
         * Nothing to choose from while the tenant cannot be asked. The value
         * still renders, as plain text — and the reason is given as
         * `disabledReason` rather than as a `footnote`, because a footnote
         * lives inside the picker's popover and a disabled field never opens
         * one. An unexplained inert control reads as broken.
         */
        disabled={!canUpdate || !xeroConnected}
        {...(canUpdate && !xeroConnected
          ? {
              disabledReason: 'Xero is not connected, so branding themes can’t be listed.',
            }
          : {})}
        options={brandingThemeOptions(brandingThemes.themes, value === '' ? null : value, null)}
        optionsLoading={brandingThemes.loading}
        emptyOptionLabel="Organisation default"
        placeholder={brandingThemes.loading ? 'Loading branding themes…' : 'Organisation default'}
        searchPlaceholder="Search branding themes…"
        {...(themeError === null ? {} : { footnote: themeError })}
        onSave={(next) =>
          saveField(['xeroBrandingThemeId'], {
            xeroBrandingThemeId: next === '' ? null : next,
          })
        }
      />
    );
  };

  // --- render ---------------------------------------------------------------

  const readOnlyReason = canUpdate ? null : denialReason(ability, 'update', 'invoices');

  return (
    <div className="grid min-w-0 gap-4">
      {formNotice && (
        <Notice tone="danger" role="alert" className="[overflow-wrap:anywhere]">
          {formNotice}
        </Notice>
      )}

      {readOnlyReason && (
        <p role="status" className="m-0 text-xs text-fg-muted">
          {readOnlyReason}
        </p>
      )}

      <FieldGroup title="Invoice">
        <FieldItem label="Subject" className="sm:col-span-2">
          {textField('subject', 'Subject')}
        </FieldItem>
        <FieldItem label="Status">{statusField()}</FieldItem>
        {/*
          ⚠️ "Account lead", not "Owner" — a DIFFERENT person, not the same one
          relabelled. It shows the RESOLVED lead and is read-only here because
          the owner retired lead-assignment on this document in favour of the
          company's, so it is plain text rather than an inert picker. (This
          previously said `accountLeadId` on a write is a 422; that is FALSE
          since migration 082 — see `accountLeadField` for the correction.) The
          invoice's own `owner` is still stored and still read; it is no longer
          writable from this section.
        */}
        <FieldItem label="Account lead">{accountLeadField()}</FieldItem>
        <CreditedTo creditedTo={invoice.creditedTo} accountLeadId={invoice.accountLead?.id} />
        {/*
          ⚠️ No "Project" item. The project is chosen PER LINE, on the line-items
          table; the header key is read-only to this section and structurally
          unwritable from it. See the block where the picker used to be.
        */}
        <FieldItem label="Bill date">{dateField('billDate', 'Bill date')}</FieldItem>
        <FieldItem label="Due date">{dateField('dueDate', 'Due date')}</FieldItem>
        <FieldItem label="Currency">{currencyField()}</FieldItem>
      </FieldGroup>

      {/*
        🔴 **The References `note` is GONE — owner's stricter pass, 2026-09-05.**
        It read "Customer PO is the customer's own purchase order number."

        ⚠️ The claim it carried is still true and still the one people get
        wrong: `reference` is the CUSTOMER's PO number, not `quoteNumber` and
        not `xeroQuoteNumber`. Xero's own field is overloaded with both, which
        is why it gets filled in wrongly — the label "Customer PO" is now the
        only thing saying so.
      */}
      <FieldGroup title="References">
        {/*
          ⚠️ READ-ONLY, and it used to be an `InlineField`. The column became
          server-only on the owner's ruling (2026-08-29) — the push writes it,
          and linking an invoice now fills it from the Xero document, so the
          hand-typed case has a server-side route and this client has none.

          The VALUE stays on screen: it is what finance reads out on the phone,
          and removing the control is not a reason to remove the number.

          Plain text, not a disabled `InlineField` — an inert control invites
          someone to try to open it, exactly as on the Account lead row above.
        */}
        <FieldItem label="Xero invoice number">{xeroInvoiceNumberValue()}</FieldItem>
        <FieldItem label="Customer PO">
          {textField('reference', 'Customer PO', { emptyLabel: 'Not recorded' })}
        </FieldItem>
      </FieldGroup>

      <FieldGroup
        title="Xero document"
        note={
          'Empty derives from the line’s project — not “no tracking”.'
        }
      >
        <FieldItem label="Line amounts">{lineAmountField()}</FieldItem>
        <FieldItem label="Branding theme">{brandingThemeField()}</FieldItem>
        <FieldItem label="Location override">
          {textField('xeroTrackingLocation', 'Location override', {
            emptyLabel: 'From the project',
          })}
        </FieldItem>
        <FieldItem label="Tour Type override">
          {textField('xeroTrackingTourType', 'Tour Type override', {
            emptyLabel: 'From the project',
          })}
        </FieldItem>
      </FieldGroup>

      <FieldGroup title="Terms" columns="">
        <FieldItem label="Payment and validity wording">
          {textField('terms', 'Terms', {
            multiline: true,
            rows: 5,
            emptyLabel: 'Not recorded',
            display:
              invoice.terms === null ? undefined : (
                <span className="block whitespace-pre-wrap">{invoice.terms}</span>
              ),
          })}
        </FieldItem>
      </FieldGroup>
    </div>
  );
}

/**
 * One label/value pair. A `<div>` inside a `<dl>` is valid and keeps the pair
 * together.
 *
 * The `<dt>` is NOT a label — `InlineField` names its own control, which is why
 * every field above is given a `label` as well as a heading here.
 */
function FieldItem({
  label,
  className,
  children,
}: {
  label: string;
  /** Column span, for the pairs that are a row rather than a cell. */
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cx('min-w-0', className)}>
      <dt className="text-xs text-fg-muted">{label}</dt>
      <dd className="m-0 mt-1 text-sm text-fg-secondary [overflow-wrap:anywhere]">{children}</dd>
    </div>
  );
}

function FieldGroup({
  title,
  columns = 'sm:grid-cols-2',
  note,
  children,
}: {
  title: string;
  /** `''` for a group whose single field is a block rather than a cell. */
  columns?: string;
  /** Said once, under the heading, rather than repeated on every field in it. */
  note?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="min-w-0">
      <h3 className="m-0 text-sm font-semibold text-fg">{title}</h3>
      {note && <p className="m-0 mt-1 text-xs text-fg-muted">{note}</p>}
      {/* One column below `sm` in every group — this app is tested at 320px. */}
      <dl className={cx('m-0 mt-3 grid gap-x-6 gap-y-4', columns)}>{children}</dl>
    </section>
  );
}
